import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import "./listing.css";
import Lawdata from "./lawcat.json";
import location from "../listing/location.svg";
import axios from "axios";
import config from "../../components/config/config";

const isBlank = (v) =>
  v === null || v === undefined || String(v).trim() === "" || String(v).toLowerCase() === "null";

// Photo, or a coloured initials box when the photo is missing / broken
const avatarColors = ["#FFAE00", "#6366F1", "#10B981", "#F97316", "#06B6D4", "#8B5CF6"];

function LawyerAvatar({ src, name }) {
  const [failed, setFailed] = useState(false);
  const cleanName = (name || "").trim();

  if (!isBlank(src) && !failed) {
    return <img src={src} alt={cleanName} onError={() => setFailed(true)} />;
  }

  const initials = cleanName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "A";
  const color = avatarColors[(cleanName.charCodeAt(0) || 0) % avatarColors.length];

  return (
    <div
      aria-label={cleanName}
      style={{
        width: 120,
        height: 140,
        borderRadius: 6,
        background: `${color}22`,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 40,
        fontWeight: 700,
        fontFamily: "'Figtree', sans-serif",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

const uniqueSpecialists = [...new Set(Lawdata.specialists)];

const renderStars = (rating) => (
  <div className="stars-ls">
    {[...Array(5)].map((_, i) => (
      <span key={i} className={i < Math.floor(rating) ? 'star-ls filled-ls' : 'star-ls'}>★</span>
    ))}
    <span className="rating-text-ls">({rating})</span>
  </div>
);

function Listing() {
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchCity, setSearchCity] = useState("");     // text in the box
  const [activeQuery, setActiveQuery] = useState("");   // what results are shown for
  const [selectedCategory, setSelectedCategory] = useState("");
  const [geoState, setGeoState] = useState("");
  const [geoTown, setGeoTown] = useState("");
  const [locationStatus, setLocationStatus] = useState("");

  // params: { search } from the search box, or { town, state } from geo-detect
  const fetchLawyers = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.apiBaseUrl}/auth/getmemberlisting`,
        { params }
      );
      setLawyers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status === 404) {
        setLawyers([]);
      } else {
        console.error("Failed to fetch lawyers:", error);
        setLawyers([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 1: Get coordinates -> Step 2: Reverse geocode -> Step 3: Fetch lawyers
  const detectLocationAndFetch = useCallback(() => {
    if (!navigator.geolocation) {
      fetchLawyers();
      return;
    }

    setLocationStatus("Detecting your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const geoRes = await axios.get(
            "https://nominatim.openstreetmap.org/reverse",
            {
              params: { lat: latitude, lon: longitude, format: "json" },
              headers: { "Accept-Language": "en" },
            }
          );

          const address = geoRes.data.address || {};
          const detectedTown =
            address.state_district ||
            address.city_district ||
            address.city ||
            address.town ||
            address.suburb ||
            address.village ||
            "";
          const detectedState = address.state || "";

          setGeoTown(detectedTown);
          setGeoState(detectedState);
          setLocationStatus("");

          // Same-town lawyers first, then rest of the state (backend handles order)
          fetchLawyers({ town: detectedTown, state: detectedState });
        } catch (err) {
          console.error("Reverse geocoding failed:", err);
          setLocationStatus("");
          fetchLawyers();
        }
      },
      (err) => {
        console.warn("Location permission denied or error:", err.message);
        setLocationStatus("");
        fetchLawyers();
      },
      { timeout: 8000 }
    );
  }, [fetchLawyers]);

  useEffect(() => {
    detectLocationAndFetch();
  }, [detectLocationAndFetch]);

  // Location filtering is done by the API; only category is filtered here
  const filteredLawyers = lawyers.filter((l) =>
    selectedCategory ? l.law_specialists?.includes(selectedCategory) : true
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchCity(value);
    // Cleared the box (x button / backspace) -> show everyone again
    if (value.trim() === "") {
      setActiveQuery("");
      setGeoTown("");
      setGeoState("");
      fetchLawyers();
    }
  };

  const handleSearchSubmit = () => {
    const q = searchCity.trim();
    setActiveQuery(q);
    setGeoTown("");
    setGeoState("");
    fetchLawyers(q ? { search: q } : {});
  };

  return (
    <div className="main-container">
      <div className="container-fluid"><Navbar /></div>
      <div className="container-main">
        <div className="lawyerlist-heading-top">
          <h2 className="lawyer-name-listing">Find A Lawyer</h2>
          {/* Show detected location as a subtle badge */}
          {geoTown && (
            <p className="text-muted" style={{ fontSize: "13px" }}>
              📍 Showing lawyers near <strong>{geoTown}{geoState ? `, ${geoState}` : ""}</strong>
            </p>
          )}
        </div>

        {/* Search */}
        <div className="search-bar-wrapper-ls">
          <div className="search-input-container-ls">
            <FontAwesomeIcon icon={faLocationDot} className="search-location-icon-ls" />
            <input
              type="search"
              className="search-input-ls"
              placeholder="Search District or State"
              value={searchCity}
              onChange={handleSearchChange}
              onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
            />
            <button className="search-btn-icon" onClick={handleSearchSubmit}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
          </div>
          {locationStatus && (
            <p className="text-muted mt-1" style={{ fontSize: "12px" }}>
              {locationStatus}
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="filter-wrapper-ls">
          <div className="filter-dropdown-ls">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              <option value="">Select Law Category</option>
              {uniqueSpecialists.map((item, i) => (
                <option key={i} value={item}>{item}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results */}
        <div className="listing-container-ls">
          <div className="search-header-ls">
            <h2>
              Showing Results
              {activeQuery && <> for: <span className="location-highlight-ls">{activeQuery}</span></>}
              <span className="ms-2 text-muted" style={{ fontSize: "14px" }}>
                ({filteredLawyers.length} found)
              </span>
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-5"><p>Loading lawyers...</p></div>
          ) : filteredLawyers.length === 0 ? (
            <div className="text-center py-5"><p>No lawyers found.</p></div>
          ) : (
            <div className="lawyers-grid-ls">
              {filteredLawyers.map((lawyer) => (
                <div key={lawyer.id} className="lawyer-card-ls">
                  <div className="card-content-ls">
                    <div className="lawyer-image-ls">
                      <LawyerAvatar src={lawyer.profile_img} name={lawyer.username} />
                    </div>
                    <div className="lawyer-details-ls">
                      <h3 className="lawyer-name-ls mb-0">Adv. {lawyer.username}</h3>
                      <div className="tags-ls">
                        <span className="tag-ls specialization-ls">{isBlank(lawyer.role) ? "Advocate" : lawyer.role}</span>
                      </div>
                      {renderStars(4.5)}
                      <p className="description-ls mb-0">
                        {isBlank(lawyer.about)
                          ? "Dedicated legal professional offering clear, reliable and client-focused legal guidance."
                          : lawyer.about.length > 80
                            ? `${lawyer.about.slice(0, 80)}...`
                            : lawyer.about}
                      </p>
                      <p className="location-ls mb-0">
                        <i className="icon-ls"><img src={location} alt="Location" /></i>
                        {[lawyer.town, lawyer.state].filter((v) => !isBlank(v)).join(", ") || "India"}
                      </p>
                    </div>
                    <Link to={`/listingview/${lawyer.id}`}>
                      <button className="view-profile-btn-ls">View Profile</button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Listing;